import {
  JIGSAW_WORKSPACE_URL_STORAGE_KEY,
  OFFICE_LANDING_ORIGIN_PARAM,
  OFFICE_PATH,
} from '@/common/constants';
import { t } from '@/plugins/i18n.plugin';

const WORKSPACE_SETUP_PATH = '/setup' as const;

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * Shared parsing for workspace URLs and `authentication.authority` values:
 * optional scheme, `http` → `https`, reject non-http(s) schemes.
 */
function normalizeWorkspaceUrlLike(trimmed: string): URL | null | 'not_https' {
  // Reject explicit non-http(s) schemes before prepending `https://`, otherwise e.g.
  // `ftp://host` becomes `https://ftp://host` and can parse as a bogus https URL.
  const explicitScheme = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):\/+/);
  if (explicitScheme) {
    const s = explicitScheme[1].toLowerCase();
    if (s !== 'http' && s !== 'https') {
      return 'not_https';
    }
  }
  const withScheme = /^https?:\/+/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const u = new URL(withScheme);
    if (!u.hostname) {
      return null;
    }
    if (u.protocol === 'http:') {
      u.protocol = 'https:';
      return u;
    }
    if (u.protocol === 'https:') {
      return u;
    }
    return 'not_https';
  } catch {
    return null;
  }
}

/** HTTPS origin for comparing user input with `result.authentication.authority` from /setup. */
function httpsOriginFromUrlString(urlLike: string): string | null {
  const trimmed = urlLike.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = normalizeWorkspaceUrlLike(trimmed);
  if (normalized === null || normalized === 'not_https') {
    return null;
  }
  return normalized.origin;
}

function setupAuthorityOrigin(data: unknown): string | null {
  if (data === null || typeof data !== 'object') {
    return null;
  }
  const result = (data as { result?: unknown }).result;
  if (result === null || typeof result !== 'object') {
    return null;
  }
  const auth = (result as { authentication?: unknown }).authentication;
  if (auth === null || typeof auth !== 'object') {
    return null;
  }
  const { authority } = auth as { authority?: unknown };
  if (!isNonEmptyString(authority)) {
    return null;
  }
  return httpsOriginFromUrlString(authority);
}

/**
 * Calls `GET {httpsOrigin}/setup` and accepts the workspace only if
 * `result.authentication.authority` resolves to the same HTTPS origin as `rawUserInput`
 * (trimmed, optional scheme, `http` upgraded to `https`).
 */
export async function validateWorkspaceBySetupRequest(
  httpsOrigin: string,
  rawUserInput: string
): Promise<{ ok: boolean; error?: OfficeAuthStartUrlError }> {
  const expectedOrigin = httpsOriginFromUrlString(rawUserInput);
  if (!expectedOrigin || expectedOrigin !== httpsOrigin) {
    return { ok: false, error: OfficeAuthStartUrlError.SetupResponseInvalid };
  }
  const setupUrl = new URL(WORKSPACE_SETUP_PATH, httpsOrigin).href;
  let response: Response;
  try {
    response = await fetch(setupUrl, { method: 'GET', credentials: 'omit' });
  } catch {
    return { ok: false, error: OfficeAuthStartUrlError.SetupRequestFailed };
  }
  if (!response.ok) {
    return { ok: false, error: OfficeAuthStartUrlError.SetupRequestFailed };
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    return { ok: false, error: OfficeAuthStartUrlError.SetupResponseInvalid };
  }
  const authorityOrigin = setupAuthorityOrigin(data);
  if (authorityOrigin === null) {
    return { ok: false, error: OfficeAuthStartUrlError.SetupResponseInvalid };
  }
  if (authorityOrigin !== expectedOrigin) {
    return { ok: false, error: OfficeAuthStartUrlError.SetupResponseInvalid };
  }
  return { ok: true };
}

export enum OfficeAuthStartUrlError {
  Invalid = 'invalid',
  NotHttps = 'not_https',
  SetupRequestFailed = 'setup_request_failed',
  SetupResponseInvalid = 'setup_response_invalid',
}

/**
 * Returns the stored HTTPS origin when present and well-formed, otherwise `null`.
 * The value is persisted as `u.origin` by `persistWorkspaceOriginFromAuthStartUrl`.
 */
function readValidStoredOrigin(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(JIGSAW_WORKSPACE_URL_STORAGE_KEY)?.trim();
    if (!raw) {
      return null;
    }
    const u = new URL(raw);
    if (u.protocol !== 'https:') {
      return null;
    }
    if (!u.hostname) {
      return null;
    }
    return u.origin;
  } catch {
    return null;
  }
}

/** Hostname from the stored HTTPS workspace origin (see `persistWorkspaceOriginFromAuthStartUrl`). */
export function readStoredWorkspaceHostname(): string | null {
  const origin = readValidStoredOrigin();
  if (!origin) {
    return null;
  }
  return new URL(origin).hostname;
}

/**
 * When local storage has a valid workspace, the URL to the office path on that origin.
 * Carries this page's origin so the office app can send the task pane back here on sign out.
 */
export function getStoredOfficeHref(): string | null {
  const origin = readValidStoredOrigin();
  if (!origin) {
    return null;
  }
  const officeUrl = new URL(OFFICE_PATH, origin);
  officeUrl.searchParams.set(
    OFFICE_LANDING_ORIGIN_PARAM,
    window.location.origin
  );
  return officeUrl.href;
}

export function persistWorkspaceOriginFromAuthStartUrl(
  officeAuthStartHref: string
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const u = new URL(officeAuthStartHref);
  localStorage.setItem(JIGSAW_WORKSPACE_URL_STORAGE_KEY, u.origin);
}

export function clearStoredWorkspaceOrigin(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem(JIGSAW_WORKSPACE_URL_STORAGE_KEY);
}

/** Accepts hostname or http(s) URL; http is upgraded to https. Navigates to /office/office-auth-start on that origin. */
export function resolveOfficeAuthStartUrl(
  raw: string
): string | { error: OfficeAuthStartUrlError } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { error: OfficeAuthStartUrlError.Invalid };
  }
  const normalized = normalizeWorkspaceUrlLike(trimmed);
  if (normalized === null) {
    return { error: OfficeAuthStartUrlError.Invalid };
  }
  if (normalized === 'not_https') {
    return { error: OfficeAuthStartUrlError.NotHttps };
  }
  return new URL(`${OFFICE_PATH}office-auth-start`, normalized.origin).href;
}

export function officeAuthStartUrlErrorMessage(
  error: OfficeAuthStartUrlError
): string {
  switch (error) {
    case OfficeAuthStartUrlError.Invalid:
      return t('ENTER_A_VALID_WORKSPACE_URL');
    case OfficeAuthStartUrlError.NotHttps:
      return t('ONLY_HTTPS_WORKSPACE_URLS_ARE_SUPPORTED');
    case OfficeAuthStartUrlError.SetupRequestFailed:
      return t('COULD_NOT_REACH_THAT_WORKSPACE_CHECK_THE_URL_AND_YOUR_NETWORK');
    case OfficeAuthStartUrlError.SetupResponseInvalid:
      return t('THAT_DOES_NOT_LOOK_LIKE_A_VALID_JIGSAW_WORKSPACE');
  }
}
